import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { sendPushNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

function ReviewPage({ session }) {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();

  const [application, setApplication] = useState(null);
  const [job, setJob] = useState(null);
  const [otherPersonName, setOtherPersonName] = useState('');
  const [otherPersonRole, setOtherPersonRole] = useState('');
  const [otherPersonId, setOtherPersonId] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const { data: app } = await supabase
      .from('applications')
      .select('*, jobs(*)')
      .eq('id', applicationId)
      .single();

    if (!app) { navigate('/'); return; }
    setApplication(app);
    setJob(app.jobs);

    const isApplicant = app.applicant_email === session.user.email;
    let resolvedName = '';

    if (isApplicant) {
      resolvedName = app.jobs.author;
      setOtherPersonName(resolvedName);
      setOtherPersonRole('committente');
    } else {
      const { data: applicantProfile } = await supabase
        .rpc('get_profile_by_email', { user_email: app.applicant_email });
      resolvedName = applicantProfile?.[0]?.full_name || app.applicant_email.split('@')[0];
      setOtherPersonName(resolvedName);
      setOtherPersonRole('lavoratore');
    }

    // Recupera l'id profilo della persona recensita, per poterle inviare la notifica push
    if (resolvedName) {
      const { data: profileMatch } = await supabase
        .from('profiles')
        .select('id')
        .eq('full_name', resolvedName)
        .single();
      if (profileMatch) setOtherPersonId(profileMatch.id);
    }

    const { data: existingReview } = await supabase
      .from('reviews')
      .select('id')
      .eq('application_id', applicationId)
      .eq('reviewer_email', session.user.email)
      .single();

    if (existingReview) setAlreadyReviewed(true);
    setLoading(false);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - imageFiles.length;
    const toAdd = files.slice(0, remaining);
    const oversized = toAdd.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) { alert('Alcune foto superano i 5MB.'); return; }
    setImageFiles(prev => [...prev, ...toAdd]);
    setImagePreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))]);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (stars === 0) { alert('Seleziona almeno una stella!'); return; }
    setSubmitting(true);

    let photoUrls = [];
    for (const file of imageFiles) {
      const ext = file.name.split('.').pop();
      const fileName = `${session.user.id}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('job-images').upload(fileName, file, { upsert: true });
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from('job-images').getPublicUrl(fileName);
        photoUrls.push(urlData.publicUrl);
      }
    }

    const { error } = await supabase.from('reviews').insert({
      job_id: application.job_id,
      application_id: parseInt(applicationId),
      reviewer_email: session.user.email,
      reviewed_email: otherPersonName,
      role: otherPersonRole,
      stars,
      comment: comment.trim() || null,
      photos: photoUrls.length > 0 ? photoUrls : null,
    });

    if (error) { alert('Errore: ' + error.message); setSubmitting(false); return; }

    // Traccia "recensione_lasciata" — ultimo step del funnel, chiude il ciclo transazione→feedback
    trackEvent('recensione_lasciata', {
      application_id: parseInt(applicationId),
      job_id: application.job_id,
      reviewed_role: otherPersonRole,
      stars,
      has_comment: !!comment.trim(),
      has_photos: photoUrls.length > 0,
    });

    // Notifica push: hai ricevuto una nuova recensione
    if (otherPersonId) {
      sendPushNotification({
        userId: otherPersonId,
        title: '⭐ Nuova recensione ricevuta!',
        body: `Hai ricevuto ${stars} stell${stars === 1 ? 'a' : 'e'} per "${job?.title}"`,
        url: `/utente/${otherPersonId}`,
      });
    }

    navigate(`/chat/app_${applicationId}`, { replace: true });
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <p>Caricamento...</p>
    </div>
  );

  if (alreadyReviewed) return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family, padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: theme.spacing.lg }}>✅</div>
      <h2 style={{ color: theme.colors.primary }}>Recensione già inviata!</h2>
      <p style={{ color: theme.colors.textSecondary }}>Hai già lasciato una recensione per questo lavoro.</p>
      <button onClick={() => navigate(`/chat/app_${applicationId}`)}
        style={{ marginTop: theme.spacing.xxl, padding: '12px 32px', background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.md, fontSize: theme.font.size.xl, cursor: 'pointer' }}>
        Torna alla chat
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family, minHeight: '100vh', background: theme.colors.background }}>

      <div style={{ background: theme.colors.primary, padding: '24px 20px', color: theme.colors.white, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
        <h1 style={{ margin: 0, fontSize: theme.font.size.xxl }}>Lascia una recensione</h1>
        <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: theme.font.size.md }}>
          Come è andata con <strong>{otherPersonName}</strong>?
        </p>
      </div>

      <div style={{ padding: theme.spacing.xxl }}>

        <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.xl, border: `1px solid ${theme.colors.border}` }}>
          <p style={{ margin: 0, fontSize: theme.font.size.base, color: '#888' }}>Lavoro completato</p>
          <p style={{ margin: '4px 0 0', fontSize: theme.font.size.xl, fontWeight: 700, color: '#222' }}>📋 {job?.title}</p>
          <p style={{ margin: '4px 0 0', fontSize: theme.font.size.base, color: theme.colors.textSecondary }}>
            Stai recensendo: <strong>{otherPersonRole === 'lavoratore' ? '👷 Lavoratore' : '💼 Committente'}</strong>
          </p>
        </div>

        <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: theme.spacing.xxl, marginBottom: theme.spacing.lg, border: `1px solid ${theme.colors.border}`, textAlign: 'center' }}>
          <p style={{ margin: '0 0 16px', fontSize: theme.font.size.xl, fontWeight: 600, color: theme.colors.textPrimary }}>
            Valuta <strong>{otherPersonName}</strong> *
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
            {[1, 2, 3, 4, 5].map(star => (
              <span key={star} onClick={() => setStars(star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                style={{ fontSize: 48, cursor: 'pointer', color: star <= (hoveredStar || stars) ? theme.colors.starGold : '#ddd', transition: 'color 0.15s' }}>★</span>
            ))}
          </div>
          <p style={{ margin: 0, fontSize: theme.font.size.md, color: '#888', height: 20 }}>
            {stars === 1 && '😞 Pessimo'}
            {stars === 2 && '😕 Scarso'}
            {stars === 3 && '😐 Nella norma'}
            {stars === 4 && '😊 Buono'}
            {stars === 5 && '🤩 Eccellente!'}
          </p>
        </div>

        <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: theme.spacing.xl, marginBottom: theme.spacing.lg, border: `1px solid ${theme.colors.border}` }}>
          <p style={{ margin: '0 0 10px', fontSize: theme.font.size.lg, fontWeight: 600, color: theme.colors.textPrimary }}>
            Commento <span style={{ fontWeight: 400, color: theme.colors.textMuted, fontSize: theme.font.size.base }}>(facoltativo)</span>
          </p>
          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Racconta com'è andata con questa persona..."
            maxLength={500} rows={4}
            style={{ width: '100%', border: `1px solid ${theme.colors.borderInput}`, borderRadius: theme.radius.sm, padding: 12, fontSize: theme.font.size.md, resize: 'none', boxSizing: 'border-box', outline: 'none', fontFamily: theme.font.family }}
          />
          <p style={{ margin: '6px 0 0', fontSize: theme.font.size.sm, color: '#bbb', textAlign: 'right' }}>{comment.length}/500</p>
        </div>

        <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, padding: theme.spacing.xl, marginBottom: theme.spacing.xxl, border: `1px solid ${theme.colors.border}` }}>
          <p style={{ margin: '0 0 6px', fontSize: theme.font.size.lg, fontWeight: 600, color: theme.colors.textPrimary }}>
            📸 Foto del lavoro svolto <span style={{ fontWeight: 400, color: theme.colors.textMuted, fontSize: theme.font.size.base }}>(facoltativo)</span>
          </p>
          <p style={{ margin: '0 0 12px', fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
            Aggiungi foto per mostrare il risultato — saranno visibili nel profilo pubblico
          </p>
          {imagePreviews.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: theme.spacing.sm, marginBottom: 10 }}>
              {imagePreviews.map((preview, index) => (
                <div key={index} style={{ position: 'relative', borderRadius: theme.radius.sm, overflow: 'hidden', aspectRatio: '1' }}>
                  <img src={preview} alt={`foto ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button onClick={() => handleRemoveImage(index)} style={{
                    position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: theme.colors.white,
                    border: 'none', borderRadius: theme.radius.circle, width: 22, height: 22,
                    fontSize: theme.font.size.sm, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {imageFiles.length < 5 && (
            <div onClick={() => document.getElementById('review-images-input').click()}
              style={{ border: `2px dashed ${theme.colors.borderInput}`, borderRadius: theme.radius.md, padding: theme.spacing.lg, textAlign: 'center', cursor: 'pointer', background: theme.colors.background }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
              <div style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted }}>
                {imageFiles.length === 0 ? 'Aggiungi foto del lavoro' : `Aggiungi altre foto (${imageFiles.length}/5)`}
              </div>
              <div style={{ fontSize: theme.font.size.xs, color: '#bbb', marginTop: 2 }}>Max 5 foto, 5MB ciascuna</div>
            </div>
          )}
          <input id="review-images-input" type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImagesChange} />
        </div>

        <button onClick={handleSubmit} disabled={submitting || stars === 0}
          style={{
            width: '100%', padding: '16px', background: stars === 0 ? '#ccc' : theme.colors.primary,
            color: theme.colors.white, border: 'none', borderRadius: theme.radius.lg, fontSize: 17,
            fontWeight: 700, cursor: stars === 0 ? 'not-allowed' : 'pointer'
          }}>
          {submitting ? 'Invio in corso...' : '⭐ Invia recensione'}
        </button>

        <button onClick={() => navigate(`/chat/app_${applicationId}`)}
          style={{ width: '100%', marginTop: 12, padding: '14px', background: 'transparent', color: theme.colors.textMuted, border: 'none', fontSize: theme.font.size.lg, cursor: 'pointer' }}>
          Salta per ora
        </button>

      </div>
    </div>
  );
}

export default ReviewPage;